import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { differenceInDays, parseISO } from 'date-fns';
import { getAccommodationSearchBoxPopover } from 'modules/accommodation/selectors';
import { AccommodationState, setAccommodationSearchBoxPopover } from 'modules/accommodation/slice';
import { showHotelDatePicker as openDatePickerOverlay, showHotelGuestPicker as openGuestPickerOverlay } from 'modules/overlay/actions';
import { DateGuestPickerSource } from 'modules/overlay/types';
import { getIsMultiRoomBundlesSearch } from 'modules/search/selectors';
import { getSearchBoxValues } from 'modules/searchBox/selectors';
import { dateStringToMiddayDate } from 'utils';
import { scrollTo } from 'utils/scroll';
import { ButtonGroup } from '@daedalus/atlas/ButtonGroup';
import { Card } from '@daedalus/atlas/Card/Card';
import { Icon } from '@daedalus/atlas/Icon';
import { InputButton } from '@daedalus/atlas/InputButton';
import { Text } from '@daedalus/atlas/Text';
import { useDispatchTeamEvent, useTrackingTeam } from '@daedalus/core/src/analytics/components/TeamContext';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { DatePickerType } from '@daedalus/core/src/datePicker/types';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { guestPickerMessages } from '@daedalus/shared/src/search/GuestPicker/guestPickerMessages';
import { DateGuestPopovers } from '../DateGuestPopovers/DateGuestsPopovers';
import { DateGuestInputLoading } from './DateGuestInputLoading';
const _exp = /*#__PURE__*/() => Text;
const NumberOfDetails = /*#__PURE__*/styled(_exp())({
  name: "NumberOfDetails",
  class: "nlx3ai3",
  propsAsIs: true
});
const GuestElementText = /*#__PURE__*/styled('span')({
  name: "GuestElementText",
  class: "gtyo0h2",
  propsAsIs: false
});
const PopoverAnchor = /*#__PURE__*/styled('div')({
  name: "PopoverAnchor",
  class: "p1svbjl0",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => InputButton;
const StyledInputButton = /*#__PURE__*/styled(_exp3())({
  name: "StyledInputButton",
  class: "spc31hk",
  propsAsIs: true
});
const DatesWrapper = /*#__PURE__*/styled('div')({
  name: "DatesWrapper",
  class: "d1o6xmty",
  propsAsIs: false
});
interface Props {
  source: DateGuestPickerSource;
  isMobile?: boolean;
  hotelHasImages?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}
export const DateGuestInput = ({
  source,
  hotelHasImages = true,
  isMobile = false,
  containerRef
}: Props) => {
  const dispatch = useDispatch();
  const dispatchTeamEvent = useDispatchTeamEvent();
  const team = useTrackingTeam();
  const accommodationSearchBoxPopover = useSelector(getAccommodationSearchBoxPopover);
  const [currentPopover, setCurrentPopover] = useState<AccommodationState['searchBoxPopover']>();
  const anchorRef = useRef<HTMLDivElement>(null);
  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch);
  const {
    checkIn,
    checkOut,
    rooms
  } = useSelector(getSearchBoxValues);
  const numberOfNights = differenceInDays(parseISO(checkOut), parseISO(checkIn));
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms
  } = roomsToConfigurationObject(rooms);
  const numberOfGuests = numberOfAdults + numberOfChildren;
  useEffect(() => {
    if (!accommodationSearchBoxPopover) {
      setCurrentPopover(null);
      if (containerRef && containerRef.current) {
        containerRef.current.style.zIndex = '1';
      }
    } else {
      scrollTo({
        behavior: 'smooth',
        top: hotelHasImages ? 535 : 9
      }).then(() => {
        if (containerRef && containerRef.current) {
          containerRef.current.style.zIndex = '101';
        }
        setCurrentPopover(accommodationSearchBoxPopover);
      });
    }
  }, [accommodationSearchBoxPopover, hotelHasImages, containerRef]);
  const closePopovers = useCallback(() => {
    dispatch(setAccommodationSearchBoxPopover(null));
  }, [setCurrentPopover]);
  const openGuestPicker = useCallback((): void => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.GuestPickerInput,
      action: Action.Clicked,
      payload: {
        source
      }
    }));
    if (isMobile) {
      dispatch(openGuestPickerOverlay(team, source));
    } else {
      dispatch(setAccommodationSearchBoxPopover('guestPicker'));
    }
  }, [team, dispatch, dispatchTeamEvent, source, isMobile]);
  const openDatePicker = useCallback((): void => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.DatePickerInput,
      action: Action.Clicked,
      payload: {
        source
      }
    }));
    if (isMobile) {
      dispatch(openDatePickerOverlay(DatePickerType.CheckIn, team, source));
    } else {
      dispatch(setAccommodationSearchBoxPopover('datePicker'));
    }
  }, [team, dispatch, dispatchTeamEvent, source, isMobile]);

  //Prevent showing the undefined date.
  if (!checkIn) return <DateGuestInputLoading />;
  return <>
      <PopoverAnchor ref={anchorRef} data-id="dateGuestInput">
        <Card borderRadius="lg">
          <ButtonGroup>
            <StyledInputButton id="openDatePicker" onClick={openDatePicker} className={cx(currentPopover === 'datePicker' && '__active')}>
              <Icon name="Calendar" colorPath="content.neutral.c600" />
              <div>
                <DatesWrapper>
                  <Text variant="titleS" as="span">
                    <FormattedDate value={dateStringToMiddayDate(checkIn)} day="2-digit" month="short" />
                  </Text>
                  <Text variant="titleS" as="span">
                    {' - '}
                  </Text>
                  <Text variant="titleS" as="span">
                    <FormattedDate value={dateStringToMiddayDate(checkOut)} day="2-digit" month="short" />
                  </Text>
                </DatesWrapper>

                <NumberOfDetails variant="bodyXS" align="left">
                  {checkOut ? <FormattedMessageWrapper id="numberOfNights" defaultMessage="{count, plural, one {1 night} other {# nights}}" values={{
                  count: numberOfNights
                }} /> : <div>&nbsp;</div>}
                </NumberOfDetails>
              </div>
            </StyledInputButton>
            <StyledInputButton id="openGuestPicker" onClick={openGuestPicker} className={cx(currentPopover === 'guestPicker' && '__active')}>
              <Icon name="User" colorPath="content.neutral.c600" />
              <div>
                <Text variant="titleS" align="left">
                  <GuestElementText>
                    <FormattedMessageWrapper {...guestPickerMessages.guest} values={{
                    count: numberOfGuests
                  }} />
                  </GuestElementText>
                </Text>
                {!isMultiRoomBundlesSearch && <NumberOfDetails variant="bodyXS" align="left">
                    <FormattedMessageWrapper id="numberOfRooms" defaultMessage="{count, plural, one {1 room} other {# rooms}}" values={{
                  count: numberOfRooms
                }} />
                  </NumberOfDetails>}
              </div>
            </StyledInputButton>
          </ButtonGroup>
        </Card>
      </PopoverAnchor>
      {!isMobile && <DateGuestPopovers currentPopover={currentPopover} closePopovers={closePopovers} anchorRef={anchorRef} />}
    </>;
};

require("./DateGuestInput.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./DateGuestInput.tsx");